import { track } from "@/lib/track";
import { useParams, useLocation, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Phone, MapPin } from "lucide-react";
import { Helmet } from "react-helmet-async";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SubmissionForm from "@/components/SubmissionForm";
import { useUI } from "@/context/UIContext";
import { btnPrimary, btnOutline, overline } from "@/components/kit";
import { CONTACT } from "@/data/site";
import { SERVICE_CITIES, SERVICE_CITY_ORDER } from "@/data/service-cities";

const SERVICES_META = {
  "fete-de-noel-entreprise": {
    key: "fetesNoel",
    label: "Fête de Noël d'entreprise",
    subtitle: "Soirée de fin d'année, arbre de Noël, cocktail entreprise",
    heroImage: "https://images.pexels.com/photos/1303080/pexels-photo-1303080.jpeg?auto=compress&cs=tinysrgb&w=1920",
    itemsLabel: "Lieux d'exception",
    itemsKey: "venues",
    parentSlug: "fetes-noel-entreprise",
    keywords: (city) =>
      `fête de Noël entreprise ${city}, organisation Noël entreprise ${city}, arbre de Noël CE ${city}, soirée fin d'année entreprise ${city}, cocktail Noël entreprise ${city}, agence événementielle Noël ${city}, animation Noël collaborateurs ${city}`,
    description: (city, r) =>
      `Organisation de fêtes de Noël d'entreprise à ${city} (${r}) — arbres de Noël, soirées de fin d'année, cocktails d'entreprise. Lieux privatisés, animations sur-mesure. Devis 24h.`,
  },
  "team-building-entreprise": {
    key: "teamBuilding",
    label: "Team Building entreprise",
    subtitle: "Cohésion, animation, séminaire d'intégration",
    heroImage: "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1920",
    itemsLabel: "Activités signature",
    itemsKey: "activities",
    parentSlug: "team-building",
    keywords: (city) =>
      `team building ${city}, team building entreprise ${city}, animation team building ${city}, séminaire team building ${city}, agence team building ${city}, cohésion équipe ${city}, activité team building ${city}, team building corporate ${city}`,
    description: (city, r) =>
      `Team building entreprise à ${city} (${r}) — activités cohésion, séminaires d'intégration, animations sur-mesure. Programme clé-en-main, coordination locale. Devis 24h.`,
  },
};

export default function ServiceCity() {
  const { city } = useParams();
  const { pathname } = useLocation();
  const service = pathname.startsWith("/team-building-entreprise") ? "team-building-entreprise" : "fete-de-noel-entreprise";
  const { setCallbackOpen } = useUI();
  const meta = SERVICES_META[service];
  const c = SERVICE_CITIES[city];
  if (!meta || !c) return <Navigate to="/prestations" replace />;

  const data = c[meta.key];
  const items = data?.[meta.itemsKey] || [];

  const idx = SERVICE_CITY_ORDER.indexOf(city);
  const nextCity = SERVICE_CITIES[SERVICE_CITY_ORDER[(idx + 1) % SERVICE_CITY_ORDER.length]];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "EventPlanner"],
    "@id": `https://desir-events.com/${service}/${city}#business`,
    name: `Désir Events — ${meta.label} à ${c.name}`,
    description: meta.description(c.name, c.region),
    url: `https://desir-events.com/${service}/${city}`,
    logo: "https://desir-events.com/desir-events-wordmark.png",
    telephone: `+33${CONTACT.phoneMobile.replace(/\D/g, "").slice(1)}`,
    priceRange: "€€€",
    areaServed: { "@type": "City", name: c.name, containedInPlace: { "@type": "AdministrativeArea", name: c.region } },
    ...(c.coords && { geo: { "@type": "GeoCoordinates", latitude: c.coords.lat, longitude: c.coords.lng } }),
    address: {
      "@type": "PostalAddress",
      streetAddress: "129 rue de Servient",
      addressLocality: "Lyon",
      postalCode: "69003",
      addressCountry: "FR",
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
    },
  };

  return (
    <>
      <Seo
        title={`${meta.label} à ${c.name}`}
        description={meta.description(c.name, c.region)}
        keywords={meta.keywords(c.name)}
        path={`/${service}/${city}`}
      />
      <Helmet><script type="application/ld+json">{JSON.stringify(jsonLd)}</script></Helmet>

      {/* Hero */}
      <section className="relative h-[75vh] min-h-[520px] overflow-hidden pt-20" data-testid="service-city-hero">
        <img src={meta.heroImage} alt={`${meta.label} à ${c.name}`} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/20" />
        <div className="relative z-10 mx-auto flex h-full max-w-[1440px] flex-col justify-end px-5 pb-16 md:px-10 md:pb-24">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-[0.28em] font-semibold text-lime">{meta.subtitle}</span>
            <span className="flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[11px] text-white backdrop-blur"><MapPin size={11} className="text-lime" /> {c.name}</span>
          </motion.div>
          <motion.h1 initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="mt-4 max-w-5xl font-serif text-5xl font-medium leading-[0.95] tracking-tight text-white md:text-7xl lg:text-8xl">
            {meta.label}<br /><span className="italic text-lime">à {c.name}</span>
          </motion.h1>
          <p className="mt-6 max-w-3xl font-serif text-2xl italic text-white/90 md:text-3xl">{data.hook}</p>
        </div>
      </section>

      {/* Context + CTA */}
      <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28">
        <div className="grid gap-16 md:grid-cols-12">
          <div className="md:col-span-7">
            <Reveal><span className={overline}>Pourquoi {c.name} ?</span></Reveal>
            <Reveal delay={1}>
              <p className="mt-6 font-serif text-2xl leading-relaxed text-ink md:text-3xl">{data.context}</p>
            </Reveal>
            <Reveal delay={2}>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/devis" className={btnPrimary} data-testid="service-city-devis-btn">Parlons de votre projet <ArrowRight size={16} /></Link>
                <button onClick={() => { track("click_rappel"); setCallbackOpen(true); }} className={btnOutline}>Être rappelé</button>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <div className="md:sticky md:top-32 rounded-2xl border border-ink/10 bg-mist p-8">
              <span className={overline}>Chef de projet dédié</span>
              <h3 className="mt-4 font-serif text-3xl text-ink">Un projet<br />à {c.name} ?</h3>
              <p className="mt-4 text-sm leading-relaxed text-ink/60">Réponse sous 24h avec une première proposition et une estimation budgétaire.</p>
              <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} className="mt-6 flex items-center gap-3 rounded-xl border border-ink/15 bg-white p-4 transition-colors hover:border-lime-dim/60" data-testid="service-city-phone">
                <Phone size={18} className="text-lime-dim" />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.15em] text-ink/40">24 h ouvrées</div>
                  <div className="mt-0.5 font-serif text-xl text-ink">{CONTACT.phoneMobile}</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Items grid */}
      <section className="border-t border-ink/10 bg-mist py-20 md:py-28" data-testid="service-city-items">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <Reveal><span className={overline}>{meta.itemsLabel} à {c.name}</span></Reveal>
          <Reveal delay={1}>
            <h2 className="mt-6 max-w-3xl font-serif text-4xl font-medium leading-tight tracking-tight text-ink md:text-5xl">
              {meta.key === "fetesNoel" ? <>Nos lieux privilégiés<br />pour votre <span className="italic text-lime-dim">Noël d'entreprise</span></> : <>Nos activités signature<br />à <span className="italic text-lime-dim">{c.name}</span></>}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-ink/10 md:grid-cols-2 lg:grid-cols-3">
            {items.map((it, i) => (
              <Reveal key={i} delay={i % 3}>
                <div className="h-full bg-white p-8" data-testid={`item-${i}`}>
                  <span className="font-serif text-3xl text-lime-dim">{String(i + 1).padStart(2, "0")}</span>
                  <p className="mt-4 text-base leading-relaxed text-ink/80">{it}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section className="border-t border-ink/10 bg-white" data-testid="service-city-form-section">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:grid-cols-12 md:px-10 md:py-28">
          <div className="md:col-span-5">
            <Reveal><span className={overline}>Votre {meta.label.toLowerCase()} à {c.name}</span></Reveal>
            <Reveal delay={1}>
              <h2 className="mt-6 font-serif text-4xl font-medium leading-tight tracking-tight text-ink md:text-5xl">
                Un projet à {c.name} ?<br /><span className="italic text-lime-dim">Réponse sous 24h.</span>
              </h2>
            </Reveal>
            <Reveal delay={2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-ink/60">
                Nombre de participants, dates envisagées, budget, objectif : décrivez-nous votre projet, nous revenons vers vous sous 24h avec une première proposition.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <div className="rounded-2xl border border-ink/10 bg-mist p-8 md:p-10">
              <SubmissionForm
                type="devis"
                testid={`service-city-form-${service}-${city}`}
                submitLabel="Envoyer ma demande"
                fields={[
                  { name: "name", label: "Votre nom", required: true },
                  { name: "company", label: "Entreprise", required: true },
                  { name: "email", label: "Email", type: "email", required: true },
                  { name: "phone", label: "Téléphone", type: "tel" },
                  { name: "message", label: `Votre projet à ${c.name}`, type: "textarea", full: true, required: true, placeholder: "Type d'événement, date envisagée, nombre d'invités, budget indicatif…" },
                ]}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Next city */}
      <section className="border-t border-ink/10 bg-ink text-white">
        <Link to={`/${service}/${nextCity.name.toLowerCase()}`} className="group mx-auto flex max-w-[1440px] items-center justify-between px-5 py-16 md:px-10 md:py-24" data-testid="next-service-city">
          <div>
            <span className="text-[11px] uppercase tracking-[0.28em] font-semibold text-lime">Ville suivante</span>
            <h3 className="mt-3 font-serif text-4xl font-medium text-white group-hover:text-lime md:text-6xl">
              {meta.label}<br />à {nextCity.name}
            </h3>
          </div>
          <ArrowUpRight size={44} className="text-white/40 transition-transform duration-300 group-hover:translate-x-2 group-hover:-translate-y-2 group-hover:text-lime" />
        </Link>
      </section>
    </>
  );
}
